interface StructuredDataProps {
  data: Record<string, unknown> | Record<string, unknown>[];
}

// Escape characters that could prematurely close the <script> tag or break out
// of the JSON-LD block (HTML comment / CDATA / U+2028/U+2029).
const safeJsonLd = (data: unknown) =>
  JSON.stringify(data)
    .replace(/</g, "\\u003c")
    .replace(/>/g, "\\u003e")
    .replace(/&/g, "\\u0026")
    .replace(/\u2028/g, "\\u2028")
    .replace(/\u2029/g, "\\u2029");

export function StructuredData({ data }: StructuredDataProps) {
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: safeJsonLd(data) }}
    />
  );
}

export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Online Sale Specialist",
  url: "https://onlinesalespecialist.nl",
  logo: "https://onlinesalespecialist.nl/favicon.png",
  description:
    "Ik regel alles rondom je website, webshop en online vindbaarheid. Zodat jij je kunt focussen op waar je goed in bent.",
  telephone: "+31592887788",
  email: "info@onlinesalespecialist.nl",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Assen",
    addressCountry: "NL",
  },
  sameAs: [
    "https://www.linkedin.com/company/online-sale-specialist/",
    "https://www.instagram.com/onlinesalespecialist",
    "https://www.facebook.com/onlinesalespecialist",
  ],
};

export const webSiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Online Sale Specialist",
  url: "https://onlinesalespecialist.nl",
};

export const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  "@id": "https://onlinesalespecialist.nl/#localbusiness",
  name: "Online Sale Specialist",
  url: "https://onlinesalespecialist.nl",
  image: "https://onlinesalespecialist.nl/favicon.png",
  logo: "https://onlinesalespecialist.nl/favicon.png",
  telephone: "+31592887788",
  email: "info@onlinesalespecialist.nl",
  priceRange: "€€",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Assen",
    addressRegion: "Drenthe",
    addressCountry: "NL",
  },
  areaServed: "NL",
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "09:00",
      closes: "17:00",
    },
  ],
  sameAs: [
    "https://www.linkedin.com/company/online-sale-specialist/",
    "https://www.instagram.com/onlinesalespecialist",
    "https://www.facebook.com/onlinesalespecialist",
  ],
};

export function buildArticleSchema(article: {
  title: string;
  description?: string;
  image?: string | null;
  datePublished?: string | null;
  dateModified?: string | null;
  slug: string;
  section: "blog" | "nieuws";
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    ...(article.description && { description: article.description }),
    ...(article.image && { image: article.image }),
    ...(article.datePublished && { datePublished: article.datePublished }),
    ...(article.dateModified && { dateModified: article.dateModified }),
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://onlinesalespecialist.nl/${article.section}/${article.slug}`,
    },
    author: {
      "@type": "Organization",
      name: "Online Sale Specialist",
    },
    publisher: {
      "@type": "Organization",
      name: "Online Sale Specialist",
      logo: {
        "@type": "ImageObject",
        url: "https://onlinesalespecialist.nl/favicon.png",
      },
    },
  };
}

export function buildBreadcrumbSchema(
  items: { name: string; url: string }[]
) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

export function buildServiceSchema(service: {
  name: string;
  description: string;
  url: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: service.name,
    description: service.description,
    url: service.url,
    provider: {
      "@type": "Organization",
      name: "Online Sale Specialist",
      url: "https://onlinesalespecialist.nl",
    },
  };
}
